import { useState, useMemo } from 'react';
import toast from 'react-hot-toast';
// This hook is provided by 'src/context/DataContext.js' and supplies all game data.
import { useDaggerheartData } from '../context/DataContext';
import { DOMAIN_CARD_CONFIG } from '../config/domainCardConfig';
import { validatePermanentCardAddition } from '../utils/characterCalculations';


/**
* DomainCardManager Component
* Provides the UI for managing the character's domain card loadout and vault.
* It enforces the loadout limit of five cards and implements the "Recall Cost" mechanic.
*
* @param {object} props - The component props.
* @param {object} props.character - The current character data object.
* @param {function} props.updateCharacter - Function to update the character state (PARTIAL updates only).
* @param {function} props.onManagerClose - Callback to close the modal.
* @param {boolean} [props.isResting=false] - A boolean to indicate if the character is resting, which negates recall costs.
*/
const DomainCardManager = ({ character, updateCharacter, onManagerClose, isResting = false }) => {
 const allData = useDaggerheartData();
  // Local state to manage card movements before saving.
 const [loadout, setLoadout] = useState(character.loadout);
 const [vault, setVault] = useState(character.vault);

 /**
  * Count loadout cards that count toward the 5-card limit.
  * Cards with meta.ignoreLoadoutLimit (like Notorious) are excluded.
  */
 const countedLoadoutCards = useMemo(() => {
   return loadout.filter(cardId => {
     const normalizedKey = cardId.replace('core_domain_card_', '').replace(/_/g, '-');
     const config = DOMAIN_CARD_CONFIG[normalizedKey];
     return !config?.meta?.ignoreLoadoutLimit;
   }).length;
 }, [loadout]);


 /**
  * Moves a card from the vault to the loadout, applying a Stress cost if not resting.
  * @param {string} cardId - The ID of the card to move.
  */
 const moveToLoadout = (cardId) => {
   // Block re-addition of vault cards that have already granted permanent bonuses
   const lockError = validatePermanentCardAddition(character, cardId);
   if (lockError) {
     toast.error(lockError);
     return;
   }

   // Check loadout limit (cards with ignoreLoadoutLimit don't count)
   if (countedLoadoutCards >= 5) {
     toast.error("Loadout is full. Move a card to the vault first.");
     return;
   }
  
   const card = allData.domainCards[cardId];
   if (!isResting && card.recallCost > 0) {
     // Check for Perfect Recall specialization (reduces first recall by 1 per session)
     const hasPerfectRecall = character.subclassFeatures?.specializations?.includes('Perfect Recall');
     const perfectRecallAvailable = hasPerfectRecall && !character.perfectRecallUsedThisSession;

     // Calculate actual recall cost (reduced by 1 if Perfect Recall is available)
     const actualRecallCost = perfectRecallAvailable
       ? Math.max(0, card.recallCost - 1)
       : card.recallCost;

     // Check if we have enough stress capacity
     const newStress = character.stress + actualRecallCost;
     if (newStress > character.maxStress) {
       toast.error("Not enough available Stress to recall this card.");
       return;
     }

     // Build update object
     const updates = { stress: newStress };

     // Mark Perfect Recall as used this session if it was applied
     if (perfectRecallAvailable) {
       updates.perfectRecallUsedThisSession = true;
       toast.success(`Perfect Recall: Stress cost reduced by 1!`, {
         duration: 3000,
         icon: '📖',
       });
     }

     // Apply stress cost and Perfect Recall tracking
     updateCharacter(updates);
   }
  
   setVault(prev => prev.filter(id => id !== cardId));
   setLoadout(prev => [...prev, cardId]);
 };


 /**
  * Moves a card from the loadout to the vault.
  * @param {string} cardId - The ID of the card to move.
  */
 const moveToVault = (cardId) => {
   setLoadout(prev => prev.filter(id => id !== cardId));
   setVault(prev => [...prev, cardId]);
 };


 /**
  * Finalizes the changes, updates the main character state, and closes the modal.
  */
 const handleSaveChanges = () => {
   // Use partial update to only change loadout/vault (preserves other fields like pending bonuses)
   updateCharacter({ loadout, vault });
   onManagerClose(); // Close the modal after saving.
 };


 /**
  * A sub-component to render a single domain card in the list.
  */
 const Card = ({ cardId, action, buttonText, disabled = false }) => {
   const card = allData.domainCards[cardId];
   const [isExpanded, setIsExpanded] = useState(false);
   if (!card) return null;

   const descriptionText = card.text || card.description || '';

   return (
     <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-md">
       <div className="flex justify-between items-center">
         <div
           className="flex-grow cursor-pointer select-none"
           onClick={() => descriptionText && setIsExpanded(prev => !prev)}
         >
           <p className="font-bold">
             {card.name} <span className="text-xs font-normal text-gray-500">(Lvl {card.level})</span>
             {descriptionText && (
               <span className="ml-1 text-xs text-gray-400">{isExpanded ? '▾' : '▸'}</span>
             )}
           </p>
           <p className="text-sm text-indigo-500 capitalize">{card.domain} - {card.type}</p>
         </div>
         <button
           onClick={() => action(cardId)}
           disabled={disabled}
           className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed flex-shrink-0 ml-2"
         >
           {buttonText}
         </button>
       </div>
       {isExpanded && descriptionText && (
         <p className="mt-2 text-xs text-gray-600 dark:text-gray-300 leading-relaxed border-t border-gray-200 dark:border-gray-600 pt-2">
           {descriptionText}
         </p>
       )}
     </div>
   );
 };


 return (
   <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
     <div>
       <h3 className="text-lg font-bold mb-2">Loadout ({countedLoadoutCards}/5{loadout.length > countedLoadoutCards ? ` + ${loadout.length - countedLoadoutCards} bonus` : ''})</h3>
       <div className="space-y-2 p-2 bg-gray-50 dark:bg-gray-900 rounded-lg h-96 overflow-y-auto">
         {loadout.map(id => <Card key={id} cardId={id} action={moveToVault} buttonText="To Vault" />)}
       </div>
     </div>
     <div>
       <h3 className="text-lg font-bold mb-2">Vault ({vault.length})</h3>
       <div className="space-y-2 p-2 bg-gray-50 dark:bg-gray-900 rounded-lg h-96 overflow-y-auto">
         {vault.map(id => <Card key={id} cardId={id} action={moveToLoadout} buttonText="To Loadout" disabled={countedLoadoutCards >= 5} />)}
       </div>
     </div>
     <div className="md:col-span-2">
       <button onClick={handleSaveChanges} className="w-full mt-4 bg-green-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-green-700">
         Save Changes & Close
       </button>
     </div>
   </div>
 );
};


export default DomainCardManager;
